/* src/sections/auth/festival/themes/RepublicDayTheme.js */
import PropTypes from 'prop-types';
import { useEffect, useRef, cloneElement } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// components
import Logo from 'src/components/logo';
import AuthHelpLink from 'src/sections/auth/AuthHelpLink';

// ----------------------------------------------------------------------

// Colors Palette
const COLORS = {
    SAFFRON: '#FF9933',
    WHITE: '#FFFFFF',
    GREEN: '#138808',
    NAVY: '#000080', // Ashoka Chakra Blue
};

// Republic Day Canvas Component (Animation Engine)
function RepublicDayCanvas() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            let animationFrameId;

            // Use const for arrays that are not reassigned
            const confetti = [];
            const sparkles = [];
            let prominentChakras = [];
            const backgroundChakras = [];
            let fighterJet = null;
            let textParticles = [];

            // Set canvas size
            const resizeCanvas = () => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            };
            resizeCanvas();
            window.addEventListener('resize', resizeCanvas);

            // --- 1. Confetti Factory ---
            const createConfetti = () => {
                const colors = [COLORS.SAFFRON, COLORS.WHITE, COLORS.GREEN];
                return {
                    x: Math.random() * canvas.width,
                    y: -10,
                    size: Math.random() * 8 + 4,
                    speed: Math.random() * 2 + 1,
                    drift: Math.random() * 1 - 0.5,
                    color: colors[Math.floor(Math.random() * colors.length)],
                    rotation: Math.random() * Math.PI * 2,
                    rotationSpeed: (Math.random() - 0.5) * 0.1,
                    opacity: Math.random() * 0.6 + 0.4,
                };
            };

            const updateConfetti = (c) => {
                c.rotation += c.rotationSpeed;
                c.x += Math.sin(c.rotation) * c.drift;
                c.y += c.speed;
                if (c.y > canvas.height) {
                    c.y = -10;
                    c.x = Math.random() * canvas.width;
                }
            };

            const drawConfetti = (c) => {
                ctx.save();
                ctx.globalAlpha = c.opacity;
                ctx.translate(c.x, c.y);
                ctx.rotate(c.rotation);
                ctx.fillStyle = c.color;
                ctx.fillRect(-c.size / 2, -c.size / 2, c.size, c.size * 0.6);
                ctx.restore();
            };

            // --- 2. Sparkle Factory ---
            const createSparkle = (x, y) => ({
                x,
                y,
                size: Math.random() * 2 + 1,
                pulseSpeed: Math.random() * 0.05 + 0.02,
                pulse: Math.random() * Math.PI * 2,
            });

            const updateSparkle = (s) => {
                s.pulse += s.pulseSpeed;
            };

            const drawSparkle = (s) => {
                const intensity = Math.sin(s.pulse) * 0.5 + 0.5;
                ctx.save();
                ctx.globalAlpha = intensity;
                ctx.shadowBlur = 10;
                ctx.shadowColor = COLORS.WHITE;
                ctx.fillStyle = COLORS.WHITE;
                ctx.beginPath();
                ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
            };

            // --- 3. Background Chakra Factory ---
            const createBackgroundChakra = () => ({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                size: Math.random() * 6 + 3,
                rotation: Math.random() * Math.PI * 2,
                rotationSpeed: Math.random() * 0.02 + 0.01,
                alpha: Math.random() * 0.2 + 0.05,
            });

            const updateBackgroundChakra = (c) => {
                c.rotation += c.rotationSpeed;
            };

            const drawBackgroundChakra = (c) => {
                ctx.save();
                ctx.globalAlpha = c.alpha;
                ctx.translate(c.x, c.y);
                ctx.rotate(c.rotation);
                ctx.strokeStyle = COLORS.NAVY;
                ctx.lineWidth = 0.5;
                ctx.beginPath();
                ctx.arc(0, 0, c.size, 0, Math.PI * 2);
                ctx.stroke();
                ctx.beginPath(); ctx.moveTo(-c.size, 0); ctx.lineTo(c.size, 0); ctx.stroke();
                ctx.beginPath(); ctx.moveTo(0, -c.size); ctx.lineTo(0, c.size); ctx.stroke();
                ctx.restore();
            };

            // --- 4. Prominent Chakra Factory ---
            const createProminentChakra = (x, y) => ({
                x,
                y,
                size: Math.random() * 15 + 20,
                rotation: Math.random() * Math.PI * 2,
                rotationSpeed: (Math.random() - 0.5) * 0.01,
                floatSpeed: Math.random() * 0.2 + 0.1,
            });

            const updateProminentChakra = (c) => {
                c.rotation += c.rotationSpeed;
                c.y -= c.floatSpeed;
                if (c.y < -c.size) {
                    c.y = canvas.height + c.size;
                    c.x = Math.random() * canvas.width;
                }
            };

            const drawProminentChakra = (c) => {
                ctx.save();
                ctx.translate(c.x, c.y);
                ctx.rotate(c.rotation);

                ctx.shadowBlur = 15;
                ctx.shadowColor = COLORS.NAVY;
                ctx.strokeStyle = COLORS.NAVY;
                ctx.fillStyle = COLORS.NAVY;
                ctx.lineWidth = 2;

                ctx.beginPath();
                ctx.arc(0, 0, c.size, 0, Math.PI * 2);
                ctx.stroke();

                ctx.beginPath();
                ctx.arc(0, 0, c.size * 0.15, 0, Math.PI * 2);
                ctx.fill();

                ctx.lineWidth = 1;
                for (let i = 0; i < 24; i += 1) {
                    ctx.rotate(Math.PI / 12);
                    ctx.beginPath();
                    ctx.moveTo(0, c.size * 0.15);
                    ctx.lineTo(0, c.size);
                    ctx.stroke();
                }
                ctx.restore();
            };

            // --- 5. Fighter Jet Factory ---
            const createFighterJet = () => ({
                x: -300,
                y: canvas.height * 0.15,
                speed: 5,
                visible: false,
                cooldown: 0,
            });

            const updateFighterJet = (jet) => {
                if (jet.cooldown > 0) {
                    jet.cooldown -= 1;
                    return;
                }
                if (!jet.visible && Math.random() < 0.005) {
                    jet.visible = true;
                    jet.x = -300;
                }
                if (jet.visible) {
                    jet.x += jet.speed;
                    if (jet.x > canvas.width + 300) {
                        jet.visible = false;
                        jet.cooldown = 400;
                    }
                }
            };

            const drawFighterJet = (jet) => {
                if (!jet.visible) return;
                ctx.save();

                const trailLength = 200;
                const offsets = [-10, 0, 10];
                const colors = [COLORS.SAFFRON, COLORS.WHITE, COLORS.GREEN];

                offsets.forEach((offset, i) => {
                    const gradient = ctx.createLinearGradient(jet.x - trailLength, 0, jet.x, 0);
                    gradient.addColorStop(0, 'rgba(0,0,0,0)');
                    gradient.addColorStop(1, colors[i]);
                    ctx.fillStyle = gradient;
                    ctx.beginPath();
                    ctx.moveTo(jet.x - trailLength, jet.y + offset + (Math.random() * 2));
                    ctx.lineTo(jet.x, jet.y + offset);
                    ctx.lineTo(jet.x - trailLength, jet.y + offset + 5);
                    ctx.fill();
                });

                ctx.translate(jet.x, jet.y);
                ctx.fillStyle = '#222';
                ctx.beginPath();
                ctx.moveTo(30, 0);
                ctx.lineTo(-20, 15);
                ctx.lineTo(-20, -15);
                ctx.closePath();
                ctx.fill();
                ctx.restore();
            };

            // --- 6. Text Particle Factory ---
            const createTextParticle = (x, y, char, index) => {
                const colors = [COLORS.SAFFRON, COLORS.WHITE, COLORS.GREEN];
                return {
                    x,
                    y: y - 100,
                    targetY: y,
                    char,
                    delay: index * 12,
                    time: 0,
                    alpha: 0,
                    scale: 0,
                    color: colors[index % 3], // 0, 1, 2
                };
            };

            const updateTextParticle = (t) => {
                t.time += 1;
                if (t.time > t.delay) {
                    if (t.y < t.targetY) t.y += 8;
                    t.alpha = Math.min(1, t.alpha + 0.1);
                    t.scale = Math.min(1, t.scale + 0.1);
                }
            };

            const drawTextParticle = (t) => {
                if (t.time < t.delay) return;
                ctx.save();
                ctx.globalAlpha = t.alpha;
                ctx.translate(t.x, t.y);
                ctx.scale(t.scale, t.scale);
                ctx.font = 'bold 50px "Arial", sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.shadowBlur = 15;
                ctx.shadowColor = t.color;
                ctx.fillStyle = t.color;
                ctx.fillText(t.char, 0, 0);
                if (t.color === COLORS.WHITE) {
                    ctx.strokeStyle = 'rgba(0,0,0,0.3)';
                    ctx.lineWidth = 1;
                    ctx.strokeText(t.char, 0, 0);
                }
                ctx.restore();
            };

            // --- Initialization ---

            // 1. Confetti & Sparkles
            for (let i = 0; i < 100; i += 1) confetti.push(createConfetti());
            for (let i = 0; i < 50; i += 1) sparkles.push(createSparkle(Math.random() * canvas.width, Math.random() * canvas.height));

            // 2. Background subtle Chakras
            for (let i = 0; i < 20; i += 1) backgroundChakras.push(createBackgroundChakra());

            // 3. Prominent Foreground Chakras
            const createProminentChakras = () => {
                prominentChakras = [];
                const positions = [
                    { x: canvas.width * 0.15, y: canvas.height * 0.8 },
                    { x: canvas.width * 0.85, y: canvas.height * 0.7 },
                    { x: canvas.width * 0.25, y: canvas.height * 0.5 },
                    { x: canvas.width * 0.75, y: canvas.height * 0.9 },
                ];
                positions.forEach(pos => {
                    prominentChakras.push(createProminentChakra(pos.x, pos.y));
                });
            };
            createProminentChakras();

            // 4. Jet
            fighterJet = createFighterJet();

            // 5. Text
            const initializeText = () => {
                const text = 'REPUBLIC DAY';
                const isDesktop = canvas.width >= 900;
                const centerX = isDesktop ? canvas.width * 0.70 : canvas.width / 2;
                const centerY = canvas.height * 0.12;
                const charSpacing = canvas.width < 768 ? 25 : 40;
                const startX = centerX - (text.length * charSpacing) / 2;
                textParticles = [];
                for (let i = 0; i < text.length; i += 1) {
                    textParticles.push(createTextParticle(startX + i * charSpacing, centerY, text[i], i));
                }
            };
            setTimeout(initializeText, 50);

            // --- Animation Loop ---
            const animate = () => {
                // Dark Navy trail effect background
                ctx.fillStyle = `rgba(0, 4, 30, 0.2)`;
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // Draw Background Elements
                sparkles.forEach(s => { updateSparkle(s); drawSparkle(s); });
                backgroundChakras.forEach(c => { updateBackgroundChakra(c); drawBackgroundChakra(c); });

                // Draw Prominent Chakras (Mid-ground)
                prominentChakras.forEach(k => { updateProminentChakra(k); drawProminentChakra(k); });

                // Draw Jet (Flypast)
                updateFighterJet(fighterJet);
                drawFighterJet(fighterJet);

                // Draw Confetti (Foreground)
                confetti.forEach(c => { updateConfetti(c); drawConfetti(c); });

                // Draw Text
                textParticles.forEach(t => { updateTextParticle(t); drawTextParticle(t); });

                animationFrameId = requestAnimationFrame(animate);
            };
            animate();

            // Handle Resize
            const handleResize = () => {
                resizeCanvas();
                createProminentChakras();
                if (textParticles.length > 0) initializeText();
            };
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                cancelAnimationFrame(animationFrameId);
            };
        }
        return undefined;
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                pointerEvents: 'none', zIndex: 2,
            }}
        />
    );
}

// Main Theme Export
export default function RepublicTheme({ children }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{
            width: '100vw', height: '100vh', position: 'relative',
            display: 'flex', alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            px: { xs: 2, md: 12 },
            background: `linear-gradient(180deg, #000428 0%, #000000 100%)`,
            overflow: 'hidden',
        }}>
            <RepublicDayCanvas />
            <Card sx={{
                p: { xs: 3, sm: 4, md: 5 }, width: 1, maxWidth: 460, zIndex: 3, position: 'relative',
                bgcolor: isDark ? 'rgba(0,2,15,0.97)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(24px)', borderRadius: 3,
                border: `1px solid ${alpha(COLORS.NAVY, 0.4)}`,
                boxShadow: [
                    `0 4px 24px ${alpha('#000', 0.5)}`,
                    `0 8px 32px ${alpha(COLORS.SAFFRON, 0.15)}`,
                    `0 8px 32px ${alpha(COLORS.GREEN, 0.15)}`,
                ].join(', '),
                '&:before': {
                    content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
                    background: `linear-gradient(90deg, ${COLORS.SAFFRON} 33%, ${COLORS.WHITE} 33%, ${COLORS.WHITE} 66%, ${COLORS.GREEN} 66%)`,
                    borderRadius: '3px 3px 0 0',
                },
            }}>
                <Box sx={{ textAlign: 'center', mb: 2.5, mt: 1 }}>
                    <Box component="span" sx={{
                        display: 'inline-block', px: 2, py: 0.5, borderRadius: 10,
                        background: `linear-gradient(90deg, ${COLORS.SAFFRON}, ${COLORS.GREEN}, ${COLORS.NAVY}, ${COLORS.SAFFRON})`,
                        backgroundSize: '300% 100%',
                        animation: 'badgeShimmer 2.5s ease infinite',
                        '@keyframes badgeShimmer': { '0%': { backgroundPosition: '100% 0' }, '100%': { backgroundPosition: '-100% 0' } },
                        fontSize: '0.72rem', fontWeight: 800, letterSpacing: 2, color: '#fff', textTransform: 'uppercase',
                        boxShadow: `0 2px 12px ${alpha(COLORS.NAVY, 0.4)}`,
                    }}>Happy Republic Day</Box>
                </Box>
                <Stack alignItems="center" sx={{ mb: 2, position: 'relative', zIndex: 2 }}><Logo /></Stack>
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    {cloneElement(children, { hideHelpLink: true })}
                </Box>
            </Card>
            <AuthHelpLink sx={{
                position: 'fixed', top: '50%', right: 0,
                transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'right center',
                zIndex: 9999, bgcolor: alpha(COLORS.NAVY, 0.92), p: 1, px: 2.5,
                borderRadius: '8px 8px 0 0', boxShadow: `0 4px 20px ${alpha('#000', 0.4)}`,
                pointerEvents: 'auto', cursor: 'pointer',
                '& .MuiLink-root': { color: '#fff', fontWeight: 700, fontSize: '0.8rem', pointerEvents: 'auto', cursor: 'pointer' },
            }} />
        </Box>
    );
}

RepublicTheme.propTypes = {
    children: PropTypes.node,
};
